import React, { useState } from 'react'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  Pagination,
  Stack,
  TextField,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import FinanceAPI from 'api/ops/v2/FinanceAPI'
import { OrderPaymentRow } from 'types/api/ops/v2/finance.type'

import OrderPaymentCard from './OrderPaymentCard'

const PAGE_SIZE = 10

const STATUS_FILTERS = [
  { label: 'Processed', value: 'processed' },
  { label: 'Pending', value: 'pending' },
  { label: 'Failed', value: 'failed' }
]

interface OrderPaymentListProps {
  customerId: string | undefined
}

function OrderPaymentList({ customerId }: OrderPaymentListProps) {
  const [currentPage, setCurrentPage] = useState(1)
  const [activeStatus, setActiveStatus] = useState<string>('')
  const [searchValue, setSearchValue] = useState('')

  const offset = (currentPage - 1) * PAGE_SIZE

  const { data, isLoading } = useQuery(
    ['getUserOrderPayments', customerId, currentPage, activeStatus],
    async () => {
      return FinanceAPI.getUserOrders({
        userId: customerId ?? '',
        limit: PAGE_SIZE,
        offset,
        sort: 'desc',
        status: activeStatus || undefined
      })
    },
    {
      enabled: !!customerId,
      keepPreviousData: true
    }
  )

  const orders: OrderPaymentRow[] = data?.data?.body?.data ?? []
  const total = data?.data?.body?.total ?? 0
  const pageCount = Math.ceil(total / PAGE_SIZE)

  const filteredOrders = searchValue
    ? orders.filter(
        (o) =>
          o.order_id.toLowerCase().includes(searchValue.toLowerCase()) ||
          (o.payment?.payment_id ?? '')
            .toLowerCase()
            .includes(searchValue.toLowerCase())
      )
    : orders

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setCurrentPage(value)
  }

  const handleStatusToggle = (status: string) => {
    if (activeStatus === status) {
      setActiveStatus('')
    } else {
      setActiveStatus(status)
    }
    setCurrentPage(1)
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        border: '1px solid #E3E7EE',
        borderRadius: 2.5,
        bgcolor: '#F8FAFC',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          borderBottom: '1px solid #EDF0F5',
          flexWrap: 'wrap',
          bgcolor: '#fff'
        }}
      >
        <TextField
          size='small'
          placeholder='Search orders, IDs...'
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon sx={{ fontSize: 18, color: '#9E9E9E' }} />
              </InputAdornment>
            )
          }}
          sx={{
            minWidth: { xs: '100%', sm: 320 },
            '& .MuiOutlinedInput-root': {
              fontSize: '0.85rem',
              borderRadius: 1.5,
              bgcolor: '#fff'
            }
          }}
        />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map((sf) => (
            <Chip
              key={sf.value}
              label={sf.label}
              size='small'
              variant={activeStatus === sf.value ? 'filled' : 'outlined'}
              onClick={() => handleStatusToggle(sf.value)}
              sx={{
                height: 30,
                borderRadius: 1.5,
                fontWeight: 700,
                fontSize: '0.75rem',
                ...(activeStatus === sf.value && {
                  bgcolor: '#253140',
                  color: '#fff',
                  '&:hover': { bgcolor: '#37474F' }
                })
              }}
            />
          ))}
        </Box>
      </Box>

      {filteredOrders.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography sx={{ color: '#9E9E9E', fontSize: '0.9rem' }}>
            No orders found
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5} sx={{ bgcolor: '#F8FAFC', p: 1.5 }}>
          {filteredOrders.map((order) => (
            <OrderPaymentCard key={order.order_id} order={order} />
          ))}
        </Stack>
      )}

      {pageCount > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            px: 2,
            py: 1.5,
            borderTop: '1px solid #EDF0F5',
            flexWrap: 'wrap',
            bgcolor: '#FAFBFC'
          }}
        >
          <Typography sx={{ fontSize: '0.8rem', color: '#757575' }}>
            Showing {offset + 1} to {Math.min(offset + PAGE_SIZE, total)} of{' '}
            {total} entries
          </Typography>
          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handlePageChange}
            color='primary'
            shape='rounded'
            sx={{
              '& .MuiPaginationItem-root': {
                fontSize: '0.8rem',
                minWidth: 32,
                height: 32
              }
            }}
          />
        </Box>
      )}
    </Box>
  )
}

export default OrderPaymentList
